import { SupportPermissions } from "@redotech/redo-model/permissions";
import { Schema } from "effect";
import { ConversationId } from "../../ids/definitions";
import { MongoUserId } from "../../ids/mongo-ids";
import {
  ConversationPriority,
  ConversationResponse,
} from "../shopper-ai/conversation/conversation";
import { Message } from "../shopper-ai/conversation/message";
import { ConversationThread } from "../shopper-ai/conversation/thread";
import { AiActivityNotification } from "./ai-activity-notification";
import { TaskRealtimeNotification } from "./task-notification";
import { UserNotification } from "./user-notification";

/**
 * Schema transform: Uint8Array (UTF-8) <-> string
 * Effect doesn't have a built-in for this, only for Base64/Hex encodings.
 * Decodes bytes to string, encodes string to bytes.
 */
export const StringFromUint8Array = Schema.transform(
  Schema.Uint8ArrayFromSelf,
  Schema.String,
  {
    strict: true,
    decode: (bytes) => new TextDecoder().decode(bytes),
    encode: (str) => new TextEncoder().encode(str),
  },
).annotations({ identifier: "StringFromUint8Array" });

export const NotificationVisibility = Schema.Literal("public", "internal");
export type NotificationVisibility = typeof NotificationVisibility.Type;

export const ConnectedNotification = Schema.Struct({
  _tag: Schema.Literal("connected"),
});
export type ConnectedNotification = typeof ConnectedNotification.Type;

export const HeartbeatNotification = Schema.Struct({
  _tag: Schema.Literal("heartbeat"),
});
export type HeartbeatNotification = typeof HeartbeatNotification.Type;

export const TypingNotification = Schema.Struct({
  _tag: Schema.Literal("typing"),
  conversationId: ConversationId,
  userId: Schema.String,
  name: Schema.String,
});
export type TypingNotification = typeof TypingNotification.Type;

export const MessageCreatedNotification = Schema.Struct({
  _tag: Schema.Literal("messageCreated"),
  conversationId: ConversationId,
  message: Message,
});
export type MessageCreatedNotification = typeof MessageCreatedNotification.Type;

// Timeline event notifications
export const ConversationCreatedNotification = Schema.Struct({
  _tag: Schema.Literal("conversationCreated"),
  conversation: ConversationResponse,
});
export type ConversationCreatedNotification =
  typeof ConversationCreatedNotification.Type;

export const ConversationUpdatedNotification = Schema.Struct({
  _tag: Schema.Literal("conversationUpdated"),
  conversationId: ConversationId,
  updateType: Schema.Literal(
    "status_changed",
    "assigned",
    "priority_changed",
    "read",
    "tag_added",
    "tag_removed",
  ),
  // Include the updated value for each update type
  newStatus: Schema.optional(Schema.String),
  newAssignee: Schema.optional(Schema.NullOr(MongoUserId)),
  newPriority: Schema.optional(Schema.NullOr(ConversationPriority)),
  readBy: Schema.optional(MongoUserId),
});
export type ConversationUpdatedNotification =
  typeof ConversationUpdatedNotification.Type;

export const ThreadCreatedNotification = Schema.Struct({
  _tag: Schema.Literal("threadCreated"),
  thread: ConversationThread,
});
export type ThreadCreatedNotification = typeof ThreadCreatedNotification.Type;

export const TeamNotification = Schema.Union(
  ConnectedNotification,
  HeartbeatNotification,
  TypingNotification,
  MessageCreatedNotification,
  ConversationCreatedNotification,
  ConversationUpdatedNotification,
  ThreadCreatedNotification,
);
export type TeamNotification = typeof TeamNotification.Type;

export const TeamNotificationJson = Schema.parseJson(TeamNotification);

export const TeamNotificationFromBytes = Schema.compose(
  StringFromUint8Array,
  TeamNotificationJson,
);

export type NotificationTag = TeamNotification["_tag"];

export const notificationRequiredPermissions: Record<
  NotificationTag,
  readonly string[]
> = {
  connected: [],
  heartbeat: [],
  typing: [SupportPermissions.VIEW_SUPPORT],
  messageCreated: [SupportPermissions.VIEW_SUPPORT],
  conversationCreated: [SupportPermissions.VIEW_SUPPORT],
  conversationUpdated: [SupportPermissions.VIEW_SUPPORT],
  threadCreated: [SupportPermissions.VIEW_SUPPORT],
};

export const PublishNotificationResponse = Schema.Struct({
  success: Schema.Literal(true),
});
export type PublishNotificationResponse =
  typeof PublishNotificationResponse.Type;

export const NotifyTypingInput = Schema.Struct({
  conversationId: ConversationId,
  userId: Schema.String,
  name: Schema.String,
  visibility: NotificationVisibility,
});
export type NotifyTypingInput = typeof NotifyTypingInput.Type;

// Combined schema for all stream notification types (team + AI activity)
export { AiActivityNotification } from "./ai-activity-notification";

export const AllStreamNotification = Schema.Union(
  TeamNotification,
  AiActivityNotification,
  UserNotification,
  TaskRealtimeNotification,
);
export type AllStreamNotification = typeof AllStreamNotification.Type;

export const AllStreamNotificationJson = Schema.parseJson(
  AllStreamNotification,
);

export type AllStreamNotificationTag = AllStreamNotification["_tag"];
